import { combineReducers } from 'redux';
import { path, pathOr } from 'ramda';
import { createSelector } from 'reselect';
import {
  isLoggedInSelector,
  brandSelector,
  proxyPrefixSelector,
} from '../params';
import {
  experimentIdSelector,
  experimentVariationIdSelector,
} from '../experiment';
import { SET_COMMON_CONFIGURATION } from '../../../constants/configuration';
import { formatAPIUrl } from '../../../../utils/templater';

const isDevelopmentMode = process.env.NODE_ENV === 'development';
const DEFAULT_HOSTNAME = process.env.REACT_APP_DEFAULT_HOSTNAME;

export const initialState = {
  useBonuses: true,
  imageTemplates: {
    GAME_IMAGE: '',
    GAME_IMAGE_ALTERNATIVE: '',
    CATEGORY_LOGO: '',
    PROVIDER_LOGO: '',
    NAVIGATION_LOGO: '',
    NAVIGATION_LOGO_WITHOUT_FILE_EXTENSION: '',
  },
  sidebarMenuItemLimits: {
    categories: 5,
    providers: 0,
  },
  useSpecialOffers: true,
  contentAPIPrefix: '',
  optimizeContainerId: null,
  aggressiveIframeLaunchMode: false,
  useAMSSegments: false,
  centrifugoURL: '',
  bigWinsRefreshRate: 3000,
  deeplinkProtocol: 'nomadcasino://',
  themeSettings: {
    theme: '',
    additionalServiceClassName: '',
    mode: '',
    unmountMode: '',
  },
  operatorId: '',
  ASMSegmentsRefreshHours: 4,
  promoBonusSystemApiPrefix: '',
  unifiedSidebar: false,
};

export function commonReducer(state = initialState, { payload, type }) {
  switch (type) {
    case SET_COMMON_CONFIGURATION:
      return { ...initialState, ...payload };
    default:
      return state;
  }
}

export default combineReducers({
  common: commonReducer,
});

/*
 * Selectors
 */
export const configurationSelector = (state) =>
  path(['app', 'configuration', 'feature'], state);

export const predicates = {
  'user.isLoggedIn': isLoggedInSelector,
  'app.brand': brandSelector,
  'experiment.id': experimentIdSelector,
  'experiment.variationId': experimentVariationIdSelector,
};

export const commonConfigurationSelector = (state) =>
  pathOr(initialState, ['app', 'configuration', 'common'], state);

export const useBonusesSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.useBonuses, ['useBonuses'])
);

export const aggressiveIframeLaunchModeSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.aggressiveIframeLaunchMode, [
    'aggressiveIframeLaunchMode',
  ])
);

export const useSpecialOffersSelector = createSelector(
  commonConfigurationSelector,
  (commonConfig) => pathOr(true, ['useSpecialOffers'], commonConfig)
);

export const optimizeContainerIdSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.optimizeContainerId, ['optimizeContainerId'])
);

export const contentAPIPrefixSelector = createSelector(
  commonConfigurationSelector,
  ({ contentAPIPrefix }) =>
    formatAPIUrl(contentAPIPrefix || initialState.contentAPIPrefix)
);

export const centrifugoURLSelector = createSelector(
  commonConfigurationSelector,
  (commonConfig) => {
    const centrifugoURL =
      commonConfig?.centrifugoURL ?? initialState?.centrifugoURL;
    const hostname =
      isDevelopmentMode && DEFAULT_HOSTNAME
        ? DEFAULT_HOSTNAME
        : window.location.hostname;

    return formatAPIUrl(centrifugoURL, { ':hostname': hostname });
  }
);

export const imageTemplatesSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.imageTemplates, ['imageTemplates'])
);

export const gameImageSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.GAME_IMAGE, ['GAME_IMAGE'])
);

export const gameImageAlternativeSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.GAME_IMAGE_ALTERNATIVE, [
    'GAME_IMAGE_ALTERNATIVE',
  ])
);

export const categoryLogoImageSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.CATEGORY_LOGO, ['CATEGORY_LOGO'])
);

export const providerLogoImagePathSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.PROVIDER_LOGO, ['PROVIDER_LOGO'])
);
export const navigationLogoImagePathSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.NAVIGATION_LOGO, ['NAVIGATION_LOGO'])
);
export const navigationLogoImagePathWithoutExtensionSelector = createSelector(
  imageTemplatesSelector,
  pathOr(initialState.imageTemplates.NAVIGATION_LOGO_WITHOUT_FILE_EXTENSION, [
    'NAVIGATION_LOGO_WITHOUT_FILE_EXTENSION',
  ])
);
export const isAMSEnabledSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.useAMSSegments, ['useAMSSegments'])
);

export const bigWinsRefreshRateSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.bigWinsRefreshRate, ['bigWinsRefreshRate'])
);

export const sidebarMenuItemLimitsSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.sidebarMenuItemLimits, ['sidebarMenuItemLimits'])
);

export const themeSettingsSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.themeSettings, ['themeSettings'])
);

export const deeplinkProtocolSelector = createSelector(
  commonConfigurationSelector,
  (state) => state?.deeplinkProtocol || initialState.deeplinkProtocol
);

export const operatorIdSelector = createSelector(
  commonConfigurationSelector,
  (state) => state?.operatorId || initialState.operatorId
);

export const frontApiPrefixSelector = createSelector(
  [commonConfigurationSelector, proxyPrefixSelector],
  (commonConfig, proxyPrefix) => {
    const frontAPIPrefix = commonConfig?.frontAPIPrefix ?? '';
    return formatAPIUrl(frontAPIPrefix, {
      ':proxyPrefix': proxyPrefix,
    });
  }
);

export const AMSRefreshHoursSelector = createSelector(
  commonConfigurationSelector,
  pathOr(initialState.ASMSegmentsRefreshHours, ['ASMSegmentsRefreshHours'])
);
